import React, { useState, useEffect } from 'react';
import {
  Stack,
  Card,
  Text,
  Title,
  Button,
  Group,
  Badge,
  Table,
  Alert,
  Box,
  Divider,
  useMantineColorScheme,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import {
  IconCheck,
  IconX,
  IconInfoCircle,
  IconCrown,
  IconCircleCheck,
  IconAlertCircle,
  IconRefresh,
  IconDownload,
  IconReceipt,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { 
  userSubscriptionService
} from '../../services/userSubscriptionService';
import { 
  paymentHistoryService, 
  PaymentHistoryDTO 
} from '../../services/paymentHistoryService';
import CancelSubscriptionModal from '../modals/CancelSubscriptionModal';
import AddPaymentMethodModal from '../modals/AddPaymentMethodModal';

const BillingContent: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [addMethodOpen, setAddMethodOpen] = useState<boolean>(false);

  // ✨ NUEVO: Estados para el nuevo sistema
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [filteredPayments, setFilteredPayments] = useState<PaymentHistoryDTO[]>([]);

  // ✨ NUEVO: Cargar datos usando el nuevo endpoint
  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Usar el nuevo endpoint /api/subscriptions/status
      const statusData = await userSubscriptionService.getSubscriptionStatus();
      setSubscriptionInfo(statusData);
      
      // Cargar historial de pagos
      const payments = await paymentHistoryService.getMyPayments().catch(() => []);
      
      // Filtrar pagos por defecto (solo exitosos)
      const successfulPayments = payments.filter((p: PaymentHistoryDTO) => p.status === 'COMPLETED');
      setFilteredPayments(successfulPayments);

    } catch (error) {
      console.error('Error loading subscription data:', error);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: 'No se pudo cargar la información de suscripción',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  // ✨ NUEVO: Manejar reactivación de suscripción
  const handleReactivate = async () => {
    if (!subscriptionInfo?.subscriptionId) return;

    try {
      setSaving(true);
      await userSubscriptionService.reactivateSubscription(subscriptionInfo.subscriptionId);
      
      notifications.show({
        color: 'green',
        title: 'Suscripción reactivada',
        message: 'Tu suscripción ha sido reactivada exitosamente',
        icon: <IconCheck size={16} />,
      });
      
      // Recargar datos
      await loadSubscriptionData();
      
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo reactivar la suscripción',
        icon: <IconX size={16} />,
      });
    } finally {
      setSaving(false);
    }
  };


  // Ejecutar cancelación tras confirmación
  const handleConfirmCancelation = async () => {
    if (!subscriptionInfo?.subscriptionId) return;

    try {
      setSaving(true);
      await userSubscriptionService.cancelSubscriptionWithAccess(subscriptionInfo.subscriptionId);
      
      notifications.show({
        color: 'blue',
        title: 'Plan cancelado exitosamente',
        message: 'Tu plan ha sido cancelado pero mantienes acceso hasta la fecha de vencimiento',
        icon: <IconCheck size={16} />,
        autoClose: 8000,
      });
      
      // Recargar datos para reflejar cambios
      await loadSubscriptionData();
      
    } catch (error: any) {
      console.error('Error canceling plan:', error);
      notifications.show({
        color: 'red',
        title: 'Error al cancelar',
        message: error?.response?.data?.message || 'No se pudo cancelar la suscripción',
        icon: <IconX size={16} />,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p="xl">
        <Text>Cargando información de facturación...</Text>
      </Box>
    );
  }

  if (!subscriptionInfo) {
    return (
      <Alert color="yellow" icon={<IconAlertCircle />}>
        No se pudo cargar la información de suscripción
      </Alert>
    );
  }

  const cardStyle = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
  };

return (
    <Card withBorder radius="lg" p="lg" style={cardStyle}>
      <Stack gap="md">
        <Title order={2}>Facturación y Suscripciones</Title>

        <Stack gap="md">
          {/* Plan */}
          <Card withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <div>
                  <Group gap="sm" align="center">
                    <Text size="xl" fw={600}>
                      {subscriptionInfo.displayText || subscriptionInfo.planName}
                    </Text>
                    {subscriptionInfo.status === 'ACTIVE' && (
                      <Badge 
                        style={{
                          backgroundColor: '#BEC5A4',
                          color: '#2d2a26',
                          border: 'none'
                        }}
                        leftSection={<IconCircleCheck size={12} />}
                      >
                        Activo
                      </Badge>
                    )}
                    {subscriptionInfo.status === 'CANCELLED_WITH_ACCESS' && (
                      <Badge color="orange" leftSection={<IconInfoCircle size={12} />}>{subscriptionInfo.statusBadge}</Badge>
                    )}
                    {subscriptionInfo.status === 'FREE' && (
                      <Badge color="gray">Gratuito</Badge>
                    )}
                  </Group>
                  <Text size="sm" c="dimmed" mt={4} ta="left">
                    {subscriptionInfo.endDate ? `Vence: ${new Date(subscriptionInfo.endDate).toLocaleDateString('es-ES')}` : 'Sin fecha de vencimiento'}
                  </Text>
                </div>
                <Group>
                  {subscriptionInfo.canReactivate && (
                    <Button 
                      style={{
                        backgroundColor: '#BEC5A4',
                        color: '#2d2a26'
                      }}
                      leftSection={<IconRefresh size={16} />} 
                      onClick={handleReactivate} 
                      loading={saving}
                    >
                      Reactivar
                    </Button>
                  )}
                  {subscriptionInfo.canUpgrade && (
                    <Button
                      onClick={() => navigate('/dashboard/upgrade')}
                      style={{
                        backgroundColor: '#2d2a26',
                        color: 'white'
                      }}
                      leftSection={<IconCrown size={16} />}
                    >
                      {subscriptionInfo.upgradeText}
                    </Button>
                  )}
                </Group>
              </Group>
              {subscriptionInfo.alertMessage && (
                <Alert 
                  color={subscriptionInfo.alertColor || 'orange'} 
                  icon={<IconInfoCircle />}
                  mt="sm"
                >
                  {subscriptionInfo.alertMessage}
                </Alert>
              )}
            </Stack>
          </Card>

          {/* Método de pago */}
          <Card withBorder>
            <Stack gap="sm">
              <Title order={4} ta="left">Método de pago</Title>
              <Group justify="space-between" align="center" wrap="nowrap">
                <Text c="dimmed" size="sm" ta="left" style={{ flex: 1 }}>
                  Agrega o actualiza tu método de pago predeterminado
                </Text>
                <Button 
                  onClick={() => setAddMethodOpen(true)}
                  style={{
                    backgroundColor: '#2d2a26',
                    color: 'white'
                  }}
                >
                  Agregar método
                </Button>
              </Group>
            </Stack>
          </Card>

          {/* Facturas (historial) */}
          <Card withBorder>
            <Title order={4} mb="sm" ta="left">Facturas</Title>
            {filteredPayments.length > 0 ? (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: 'left' }}>Fecha</Table.Th>
                    <Table.Th style={{ textAlign: 'left' }}>Plan</Table.Th>
                    <Table.Th style={{ textAlign: 'left' }}>Total</Table.Th>
                    <Table.Th style={{ textAlign: 'left' }}>Estado</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Acciones</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredPayments.map((payment) => (
                    <Table.Tr key={payment.id}>
                      <Table.Td style={{ textAlign: 'left' }}>{new Date(payment.paymentDate).toLocaleDateString('es-ES')}</Table.Td>
                      <Table.Td style={{ textAlign: 'left' }}>{payment.planName || '—'}</Table.Td>
                      <Table.Td style={{ textAlign: 'left' }}>${payment.amount}</Table.Td>
                      <Table.Td style={{ textAlign: 'left' }}>
                        <Badge 
                          size="sm"
                          style={{
                            backgroundColor: '#BEC5A4',
                            color: '#2d2a26',
                            border: 'none'
                          }}
                        >
                          Pagado
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Group gap="xs" justify="flex-end">
                          <Tooltip label="Ver recibo" withArrow>
                            <ActionIcon 
                              variant="subtle" 
                              aria-label="Ver recibo" 
                              onClick={async () => {
                                try {
                                  const { data } = await paymentHistoryService.getReceipt(payment.id);
                                  const blob = new Blob([data], { type: 'text/html' });
                                  const url = URL.createObjectURL(blob);
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 100);
                                } catch (error) {
                                  console.error('Error al ver recibo:', error);
                                  notifications.show({
                                    color: 'red',
                                    title: 'Error',
                                    message: 'No se pudo cargar el recibo',
                                  });
                                }
                              }}
                            >
                              <IconReceipt size={18} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Descargar PDF" withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              aria-label="Descargar PDF"
                              onClick={async () => {
                                try {
                                  const blob = await paymentHistoryService.getReceiptPDF(payment.id);
                                  const url = URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `recibo-${payment.id}.pdf`;
                                  link.click();
                                  URL.revokeObjectURL(url);
                                  notifications.show({
                                    color: 'green',
                                    title: 'Descarga exitosa',
                                    message: 'El recibo se ha descargado correctamente',
                                  });
                                } catch (error) {
                                  console.error('Error al descargar recibo:', error);
                                  notifications.show({
                                    color: 'red',
                                    title: 'Error',
                                    message: 'No se pudo descargar el recibo',
                                  });
                                }
                              }}
                            >
                              <IconDownload size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed" ta="left" py="md">No hay facturas disponibles</Text>
            )}
          </Card>

          {/* Cancelar plan */}
          <Card withBorder>
            <Stack gap="sm">
              <Title order={4} ta="left">Cancelar plan</Title>
              {subscriptionInfo.status === 'CANCELLED_WITH_ACCESS' ? (
                <>
                  <Alert color="orange" icon={<IconInfoCircle />}>
                    Tu suscripción está cancelada. Mantienes acceso hasta{' '}
                    <strong>{new Date(subscriptionInfo.endDate).toLocaleDateString('es-ES')}</strong>.
                  </Alert>
                  <Group justify="flex-end" mt="sm">
                    {subscriptionInfo.canReactivate && (
                      <Button 
                        style={{
                          backgroundColor: '#BEC5A4',
                          color: '#2d2a26'
                        }}
                        leftSection={<IconRefresh size={16} />} 
                        onClick={handleReactivate} 
                        loading={saving}
                      >
                        Reactivar suscripción
                      </Button>
                    )}
                  </Group>
                </>
              ) : (
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Text c="dimmed" size="sm" ta="left" style={{ flex: 1 }}>
                    Puedes cancelar tu plan ahora y mantendrás acceso hasta la fecha de vencimiento.
                  </Text>
                  {subscriptionInfo.canCancel && (
                    <Button 
                      onClick={() => setCancelModalOpen(true)} 
                      loading={saving}
                      style={{
                        backgroundColor: '#fca5a5',
                        color: '#2d2a26',
                        border: 'none'
                      }}
                    >
                      Cancelar plan
                    </Button>
                  )}
                  {!subscriptionInfo.canCancel && subscriptionInfo.canReactivate && (
                    <Button 
                      style={{
                        backgroundColor: '#BEC5A4',
                        color: '#2d2a26'
                      }}
                      leftSection={<IconRefresh size={16} />} 
                      onClick={handleReactivate} 
                      loading={saving}
                    >
                      Reactivar suscripción
                    </Button>
                  )}
                </Group>
              )}
            </Stack>
          </Card>
        </Stack>

        {/* Modales */}
        <CancelSubscriptionModal
          opened={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={handleConfirmCancelation}
          subscription={subscriptionInfo}
          loading={saving}
        />
        <AddPaymentMethodModal
          opened={addMethodOpen}
          onClose={() => setAddMethodOpen(false)}
          onSuccess={() => setAddMethodOpen(false)}
        />
      </Stack>
    </Card>
  );
};

export default BillingContent;